import { GalleryController } from '@icom/stimulus-gallery';
import { breakpoints, isBelowViewportSize } from '../../utils/viewport_size_handler';
import { createVideo } from '../../utils/create_video';

export default class extends GalleryController {
  static targets = [
    'imageIndex',
    'caption',
    'thumbnailVideo',
    'thumbnailsContainer',
    'arrowButton',
    'openFullScreenButton',
    'videoButton',
    'imageCount',
    'galleryContent',
  ];
  static outlets = ['details--fullscreen-gallery'];
  static classes = ['hide'];

  static values = {
    captions: Array,
  };

  connect() {
    super.connect();
    this.isInitialized = false;
    this.switchToImage(0, true);
    this.boundOpenFullscreenGallery = this.openFullscreenGallery.bind(this);
    this.handleFullScreenGalleryOpen();
    this.setupEventListeners();
    this.handleControlsElementsVisibility();
    this.handleOnVideoClick();
  }

  recalculateLayout() {
    this.handleFullScreenGalleryOpen();
  }

  setupEventListeners() {
    this.element.addEventListener('focus', () => {
      this.registerKeyboardNavigation();
    });
    this.element.addEventListener('blur', () => {
      this.unregisterKeyboardNavigation();
    });

    this.arrowButtonTargets.forEach((arrowButton) => {
      arrowButton.addEventListener('focus', () => {
        this.registerKeyboardNavigation();
      });
      arrowButton.addEventListener('blur', () => {
        this.unregisterKeyboardNavigation();
      });
    });
  }

  afterMainImageSwitch() {
    this.imageIndexTarget.innerText = this.activeImageIndex + 1;
    if (this.hasCaptionTarget) {
      this.captionTarget.innerText = this.captionsValue[this.activeImageIndex] ?? '';
      this.captionTarget.classList.toggle(this.hideClass, !this.captionsValue[this.activeImageIndex]);
    }

    if (!this.isInitialized) {
      this.isInitialized = true;
      return;
    }

    this._updateThumbnailPositions(this.activeImageIndex);
    this.handleControlsElementsVisibility();
    this.handleOnVideoClick();
  }

  pushEvent(event) {
    window.GTM.pushEvent(event.params.event);
  }

  openFullscreenGallery(e) {
    e.stopPropagation();
    this.detailsFullscreenGalleryOutlet.openModal(this.activeImageIndex);
  }

  handleFullScreenGalleryOpen() {
    const inSiteGallery = document.getElementById('inSiteGallery');
    if (isBelowViewportSize(breakpoints.desktop)) {
      inSiteGallery.addEventListener('click', this.boundOpenFullscreenGallery);
    } else {
      inSiteGallery.removeEventListener('click', this.boundOpenFullscreenGallery);
    }
  }

  _updateThumbnailPositions(activeIndex) {
    if (activeIndex % 1 !== 0) {
      return;
    }

    const isDesktop = !isBelowViewportSize(breakpoints.desktop);
    const numberOfVisibleThumbnailsBeforeActive = 1;
    const gapInPx = 10;

    const offset = this.thumbnailTargets
      .filter((_, i) => i < activeIndex - numberOfVisibleThumbnailsBeforeActive)
      .reduce((result, thumbnail) => {
        let thumbnailSize = thumbnail.getBoundingClientRect().width;
        if (isDesktop) {
          thumbnailSize = thumbnail.getBoundingClientRect().height;
        }
        return result + thumbnailSize + gapInPx;
      }, 0);

    this.thumbnailsContainerTarget.scroll({
      top: isDesktop ? offset : undefined,
      left: isDesktop ? undefined : offset,
      behavior: 'smooth',
    });
  }

  createNonImageElement(config, element) {
    const { width, height } = this.swipeContainerTarget.getBoundingClientRect();
    return createVideo(element, config.id, width, height);
  }

  onVideoButtonClick() {
    const videoIndex = this.thumbnailTargets.length - 1;
    this.detailsFullscreenGalleryOutlet.openModal(videoIndex);
  }

  handleControlsElementsVisibility() {
    this.galleryContentTarget.classList.toggle(
      'gallery__content--video',
      this.hasThumbnailVideoTarget && this.thumbnailVideoTarget.classList.contains('active')
    );
  }

  handleOnVideoClick() {
    this.nonImageContainerTargets[1].classList.toggle(
      'pe-none',
      this.hasThumbnailVideoTarget && !this.thumbnailVideoTarget.classList.contains('active')
    );
  }
}
